import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
import { trTR as coreTrTR, enUS as coreEnUS, frFR as coreFrFR } from "@mui/material/locale";
import { trTR as gridTrTR, enUS as gridEnUS, frFR as gridFrFR } from "@mui/x-data-grid/locales";
import i18n from "./i18n/config";

const CORE_LOCALES = { tr: coreTrTR, en: coreEnUS, fr: coreFrFR };
const GRID_LOCALES = { tr: gridTrTR, en: gridEnUS, fr: gridFrFR };
/*
createTheme, Material-UI (MUI) kütüphanesinde bir temayı oluşturmak için kullanılan bir fonksiyondur. MUI, React uygulamalarında kullanılan 
popüler bir UI (kullanıcı arayüzü) kütüphanesidir ve createTheme fonksiyonu, uygulamanızın genel görünümünü ve hissini özelleştirmenize olanak
 tanır.
Bu fonksiyon, temelde bir tema nesnesi oluşturur ve bu nesne, uygulama genelinde kullanılacak renkler, tipografi, kenarlık stilleri ve
 diğer birçok stil ayarını içerir. Tema nesnesini özelleştirebilir ve ardından uygulamanızdaki bileşenlere bu temayı uygulayabilirsiniz.
*/
// color design tokens export
export const tokens = (mode) => ({//Her yerde bunu import edeceğim ve bunun üzerinden renklere ulaşacağım.
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#1a1a1a",
          200: "#333333",
          300: "#4d4d4d",
          400: "#666666",
          500: "#808080",
          600: "#999999",
          700: "#b3b3b3",
          800: "#cccccc",
          900: "#e6e6e6",
        },
        primary: {
          100: "#f5f2ed",
          200: "#ebe6dd",
          300: "#e0d9cd",
          400: "#f8f5f0", // Card/Container backgrounds (warm cream)
          500: "#ede8df", // Main background (deeper beige)
          600: "#ddd6ca",
          700: "#cac1b3",
          800: "#a59b8d",
          900: "#80776b",
        },
        greenAccent: {
          100: "#064e3b",
          200: "#065f46",
          300: "#047857",
          400: "#059669",
          500: "#10b981",
          600: "#34d399",
          700: "#6ee7b7",
          800: "#a7f3d0",
          900: "#d1fae5",
        },
        redAccent: {
          100: "#7f1d1d",
          200: "#991b1b",
          300: "#b91c1c",
          400: "#dc2626",
          500: "#ef4444",
          600: "#f87171",
          700: "#fca5a5",
          800: "#fecaca",
          900: "#fee2e2",
        },
        blueAccent: {
          100: "#1e3a8a",
          200: "#1e40af",
          300: "#2563eb",
          400: "#3b82f6",
          500: "#60a5fa",
          600: "#93c5fd",
          700: "#bfdbfe",
          800: "#dbeafe",
          900: "#eff6ff",
        },
      }),
});
// mui theme settings
/*
primary: Bu genellikle uygulamanın ana rengidir. Kullanıcı arayüzündeki ana öğeler için kullanılır. main, bu renk paletinin ana tonunu belirtir.

secondary: Bu, ikincil renktir ve genellikle uygulamanın öne çıkan diğer öğeleri veya vurgulanan alanları için kullanılır. main, bu renk paletinin ikinci tonunu belirtir.

neutral: Bu genellikle nötr renkler için kullanılır. Örneğin, gri tonları. dark, main, ve light olmak üzere üç farklı ton içerir.

background: Bu, genellikle uygulamanın genel arka plan rengidir. default, varsayılan arka plan rengini belirtir.
*/
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";
  return {
    palette: {
      mode,
      primary: {
        main: colors.primary[500],
      },
      secondary: {
        main: colors.greenAccent[500],
      },
      /** tokens.grey indeksleri moda göre: açık arka planda koyu yazı, koyu arka planda açık yazı */
      text: {
        primary: colors.grey[100],
        secondary: colors.grey[400],
      },
      background: {
        default: isDark ? colors.primary[500] : "#ffffff",
        paper: isDark ? colors.primary[600] : "#ffffff",
      },
      divider: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(15, 23, 42, 0.08)",
      ...(isDark
        ? {
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
          }
        : {
            neutral: {
              dark: colors.grey[300],
              main: colors.grey[500],
              light: colors.grey[800],
            },
          }),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            WebkitTextSizeAdjust: "100%",
            textSizeAdjust: "100%",
            overflowX: "clip",
          },
          body: {
            overflowX: "clip",
          },
          "#root": {
            overflowX: "clip",
            minWidth: 0,
          },
          p: {
            textAlign: "justify",
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            "@media (max-width: 1400px)": {
              paddingLeft: "max(16px, env(safe-area-inset-left, 0px))",
              paddingRight: "max(16px, env(safe-area-inset-right, 0px))",
            },
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          item: {
            minWidth: 0,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            overflowWrap: "anywhere",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            "@media (max-width: 1400px)": {
              whiteSpace: "normal",
            },
          },
        },
      },
      MuiModal: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
    },
    typography: {
      fontFamily: ["'Inter'", "sans-serif"].join(","),
      htmlFontSize: 16,
      fontSize: 15,
      h1: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 700,
      },
      h2: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 700,
      },
      h3: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 28, // 26'dan 28'e çıkarıldı
        fontWeight: 600,
      },
      h4: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 24, // 22'den 24'e çıkarıldı
        fontWeight: 600,
      },
      h5: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 20, // 18'den 20'ye çıkarıldı
        fontWeight: 600,
      },
      h6: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 18, // 16'dan 18'e çıkarıldı (En küçük alt başlıklar)
        fontWeight: 600,
      },
      button: {
        fontFamily: ["'Inter'", "sans-serif"].join(","),
        fontSize: 15,
        fontWeight: 600,
        textTransform: "none", 
      }
    },
  };
};

// context for color mode

export const ColorModeContext = createContext({
  toggleColorMode: () => {},//toggleColorMode fonksiyonunda arka planın rengini değişmeyi tetikleyecek fonksiyonu çağıracağım.Bunu createContext içene yazarakta useContext kullanarak sonra buna erişebileceğim.
});

/** Nested panel light tema da aynı locale paketlerini kullanır */
export function getMuiLocaleArgs(lang) {
  const code = String(lang || "en").split("-")[0];
  return [CORE_LOCALES[code] || CORE_LOCALES.en, GRID_LOCALES[code] || GRID_LOCALES.en];
}

export const useMode = () => {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem("theme_mode") || "light";
    } catch (e) {
      return "light";
    }
  });
  const [lang, setLang] = useState(() => (i18n.language || "en").split("-")[0]);

  useEffect(() => {
    const onLanguageChanged = (lng) => setLang((lng || "en").split("-")[0]);
    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, []);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          try {
            localStorage.setItem("theme_mode", next);
          } catch (e) {
            // ignore
          }
          return next;
        }),
    }),
    []
  );

  const theme = useMemo(() => {
    return createTheme(themeSettings(mode), ...getMuiLocaleArgs(lang));
  }, [mode, lang]);

  return [theme, colorMode];
};
